import React, { useRef } from 'react'
import { motion } from 'framer-motion'
import { useIntersectionObserver } from '@hooks/useIntersectionObserver'
import { useResponsiveSettings } from '@hooks/useResponsiveSettings'
import { APP_CONFIG } from '@config/index'

const Services: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null)
    const entry = useIntersectionObserver(sectionRef, { threshold: 0.1 })
    const isVisible = !!entry?.isIntersecting
    const { shouldReduceEffects } = useResponsiveSettings()

    const opticalCardHover = shouldReduceEffects
        ? undefined
        : {
            y: -15,
            scale: 1.05,
            rotateY: 5,
            transition: { duration: 0.4, type: 'spring', stiffness: 300 },
        }

    const dentalCardHover = shouldReduceEffects
        ? undefined
        : {
            y: -15,
            scale: 1.05,
            rotateY: -5,
            transition: { duration: 0.4, type: 'spring', stiffness: 300 },
        }

    const backgroundFlow = shouldReduceEffects
        ? undefined
        : {
            backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
        }

    const glowBlue = shouldReduceEffects
        ? undefined
        : {
            scale: [1, 1.05, 1],
            rotate: [0, 5, -5, 0],
        }

    const glowRose = shouldReduceEffects
        ? undefined
        : {
            scale: [1, 1.05, 1],
            rotate: [0, -5, 5, 0],
        }

    const titlePulse = shouldReduceEffects
        ? undefined
        : {
            scale: [1, 1.02, 1],
        }

    const iconBluePulse = shouldReduceEffects
        ? undefined
        : {
            boxShadow: [
                '0 10px 30px rgba(59, 130, 246, 0.3)',
                '0 10px 50px rgba(6, 182, 212, 0.5)',
                '0 10px 30px rgba(59, 130, 246, 0.3)',
            ],
        }

    const iconRosePulse = shouldReduceEffects
        ? undefined
        : {
            boxShadow: [
                '0 10px 30px rgba(244, 63, 94, 0.3)',
                '0 10px 50px rgba(236, 72, 153, 0.5)',
                '0 10px 30px rgba(244, 63, 94, 0.3)',
            ],
        }

    const badgePulse = shouldReduceEffects
        ? undefined
        : {
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5],
        }

    const badgeIconMotion = shouldReduceEffects
        ? undefined
        : {
            rotate: [0, 10, -10, 0],
            scale: [1, 1.15, 1],
            boxShadow: [
                '0 10px 30px rgba(0, 0, 0, 0.2)',
                '0 20px 50px rgba(0, 0, 0, 0.3)',
                '0 10px 30px rgba(0, 0, 0, 0.2)',
            ],
        }

    const iconBlueHover = shouldReduceEffects
        ? undefined
        : {
            rotate: [0, -15, 15, -15, 15, 0],
            scale: 1.2,
            transition: { duration: 0.6, type: 'spring' },
        }

    const iconRoseHover = shouldReduceEffects
        ? undefined
        : {
            rotate: [0, 15, -15, 15, -15, 0],
            scale: 1.2,
            transition: { duration: 0.6, type: 'spring' },
        }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    }

    return (
        <section
            ref={sectionRef}
            id="servicios"
            className="section-padding bg-gray-50"
        >
            <div className="container-fluid px-4 sm:px-6">
                <motion.div
                    className="text-center mb-12 sm:mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title mb-4 sm:mb-6">
                        Nuestros Servicios
                        <span className="block text-gradient">Especializados</span>
                    </h2>
                    <p className="section-subtitle px-4">
                        Cuidamos tu salud visual y dental con la más alta calidad profesional
                    </p>
                </motion.div>

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16"
                    variants={containerVariants}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                >
                    {/* Optical Services */}
                    <motion.div
                        className="space-y-8"
                        variants={itemVariants}
                    >
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 text-white rounded-full mb-6">
                                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                {APP_CONFIG.services.optica.title}
                            </h3>
                        </div>

                        <div className="grid gap-6">
                            {APP_CONFIG.services.optica.services.map((service) => (
                                <motion.div
                                    key={service.id}
                                    className="relative card p-6 transition-all duration-500 group overflow-hidden cursor-pointer"
                                    variants={itemVariants}
                                    whileHover={opticalCardHover}
                                    style={{
                                        transformStyle: 'preserve-3d',
                                        perspective: '1000px'
                                    }}
                                >
                                    {/* Ultra vibrant animated background layers */}
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-br from-blue-400/0 via-cyan-400/0 to-blue-500/0 group-hover:from-blue-400/30 group-hover:via-cyan-400/30 group-hover:to-blue-500/30 transition-all duration-500 rounded-2xl"
                                        animate={backgroundFlow}
                                        transition={backgroundFlow ? {
                                            duration: 8,
                                            repeat: Infinity,
                                            ease: 'linear'
                                        } : undefined}
                                    />

                                    {/* Pulsating outer glow - MUCHO MÁS VISIBLE */}
                                    <motion.div
                                        className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-70 blur-xl transition-all duration-500"
                                        animate={glowBlue}
                                        transition={glowBlue ? {
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: 'easeInOut'
                                        } : undefined}
                                    />

                                    {/* Animated border with rainbow effect */}
                                    <motion.div
                                        className="absolute -inset-0.5 opacity-0 group-hover:opacity-100 rounded-2xl transition-all duration-500"
                                        style={{
                                            background: 'linear-gradient(45deg, #3b82f6, #06b6d4, #8b5cf6, #ec4899, #3b82f6)',
                                            backgroundSize: '300% 300%',
                                        }}
                                        animate={backgroundFlow ? { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] } : undefined}
                                        transition={backgroundFlow ? {
                                            duration: 5,
                                            repeat: Infinity,
                                            ease: 'linear'
                                        } : undefined}
                                    />

                                    <div className="relative flex items-start space-x-4 bg-white rounded-2xl">
                                        <motion.div
                                            className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 text-white rounded-xl flex items-center justify-center shadow-2xl group-hover:shadow-blue-500/50 transition-all duration-300"
                                            animate={iconBluePulse}
                                            transition={iconBluePulse ? {
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: 'easeInOut'
                                            } : undefined}
                                            whileHover={iconBlueHover}
                                        >
                                            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                                                {service.icon === 'search' && (
                                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                                )}
                                                {service.icon === 'glasses' && (
                                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                )}
                                                {service.icon === 'eye-dropper' && (
                                                    <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1z" clipRule="evenodd" />
                                                )}
                                                {service.icon === 'sun' && (
                                                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                                                )}
                                            </svg>
                                        </motion.div>
                                        <div className="flex-1">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                                {service.title}
                                            </h4>
                                            <p className="text-gray-600 mb-3">
                                                {service.description}
                                            </p>
                                            <ul className="space-y-1">
                                                {service.features?.map((feature, featureIndex) => (
                                                    <li key={featureIndex} className="text-sm text-gray-500 flex items-center">
                                                        <svg className="w-4 h-4 text-primary-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Dental Services */}
                    <motion.div
                        className="space-y-8"
                        variants={itemVariants}
                    >
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-500 text-white rounded-full mb-6">
                                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                {APP_CONFIG.services.dental.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Expertos certificados en cada área de la odontología
                            </p>
                        </div>

                        {/* Banner de Especialistas Certificados - ULTRA DESTACADO */}
                        <motion.div
                            className="relative bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 rounded-2xl p-6 sm:p-8 mb-6 shadow-2xl overflow-hidden"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            {/* Animated background pattern */}
                            <div className="absolute inset-0 opacity-20">
                                <motion.div
                                    className="absolute inset-0"
                                    animate={backgroundFlow ? { backgroundPosition: ['0% 0%', '100% 100%'] } : undefined}
                                    transition={backgroundFlow ? {
                                        duration: 20,
                                        repeat: Infinity,
                                        ease: 'linear'
                                    } : undefined}
                                    style={{
                                        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                                        backgroundSize: '30px 30px',
                                    }}
                                />
                            </div>

                            {/* Glow effect */}
                            <motion.div
                                className="absolute -inset-1 bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 rounded-2xl opacity-75 blur-xl"
                                animate={badgePulse}
                                transition={badgePulse ? {
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: 'easeInOut'
                                } : undefined}
                            />

                            <div className="relative flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
                                <motion.div
                                    className="flex-shrink-0 bg-white rounded-full p-4 shadow-lg"
                                    animate={badgeIconMotion}
                                    transition={badgeIconMotion ? {
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: 'easeInOut'
                                    } : undefined}
                                >
                                    <svg className="w-12 h-12 sm:w-14 sm:h-14 text-rose-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </motion.div>
                                <div className="flex-1">
                                    <motion.h4
                                        className="text-xl sm:text-2xl font-black text-white mb-2 drop-shadow-lg"
                                        animate={titlePulse}
                                        transition={titlePulse ? {
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: 'easeInOut'
                                        } : undefined}
                                    >
                                        🏆 Especialistas Certificados
                                    </motion.h4>
                                    <p className="text-sm sm:text-base text-white font-bold drop-shadow-md">
                                        Expertos en <span className="text-yellow-200 font-black text-lg">Ortodoncia</span>, <span className="text-yellow-200 font-black text-lg">Endodoncia</span> y <span className="text-yellow-200 font-black text-lg">Periodoncia</span>
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        <div className="grid gap-6">
                            {APP_CONFIG.services.dental.services.map((service) => (
                                <motion.div
                                    key={service.id}
                                    className="relative card p-6 transition-all duration-500 group overflow-hidden cursor-pointer"
                                    variants={itemVariants}
                                    whileHover={dentalCardHover}
                                    style={{
                                        transformStyle: 'preserve-3d',
                                        perspective: '1000px'
                                    }}
                                >
                                    {/* Ultra vibrant animated background layers */}
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-br from-pink-400/0 via-rose-400/0 to-orange-500/0 group-hover:from-pink-400/30 group-hover:via-rose-400/30 group-hover:to-orange-500/30 transition-all duration-500 rounded-2xl"
                                        animate={backgroundFlow}
                                        transition={backgroundFlow ? {
                                            duration: 8,
                                            repeat: Infinity,
                                            ease: 'linear'
                                        } : undefined}
                                    />

                                    {/* Pulsating outer glow - MUCHO MÁS VISIBLE */}
                                    <motion.div
                                        className="absolute -inset-1 bg-gradient-to-r from-rose-500 via-pink-400 to-orange-500 rounded-2xl opacity-0 group-hover:opacity-70 blur-xl transition-all duration-500"
                                        animate={glowRose}
                                        transition={glowRose ? {
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: 'easeInOut'
                                        } : undefined}
                                    />

                                    {/* Animated border with rainbow effect */}
                                    <motion.div
                                        className="absolute -inset-0.5 opacity-0 group-hover:opacity-100 rounded-2xl transition-all duration-500"
                                        style={{
                                            background: 'linear-gradient(45deg, #f43f5e, #ec4899, #f97316, #facc15, #f43f5e)',
                                            backgroundSize: '300% 300%',
                                        }}
                                        animate={backgroundFlow ? { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] } : undefined}
                                        transition={backgroundFlow ? {
                                            duration: 5,
                                            repeat: Infinity,
                                            ease: 'linear'
                                        } : undefined}
                                    />

                                    <div className="relative flex items-start space-x-4 bg-white rounded-2xl">
                                        <motion.div
                                            className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 text-white rounded-xl flex items-center justify-center shadow-2xl group-hover:shadow-rose-500/50 transition-all duration-300"
                                            animate={iconRosePulse}
                                            transition={iconRosePulse ? {
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: 'easeInOut'
                                            } : undefined}
                                            whileHover={iconRoseHover}
                                        >
                                            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                                                {service.icon === 'stethoscope' && (
                                                    <path d="M2 6a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a2 2 0 11-4 0V8a1 1 0 00-.553-.894l2-1A1 1 0 0113 6h2a1 1 0 01.553.106l-1 .894z" />
                                                )}
                                                {service.icon === 'tools' && (
                                                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                )}
                                                {service.icon === 'smile' && (
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clipRule="evenodd" />
                                                )}
                                                {service.icon === 'shield-alt' && (
                                                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                )}
                                                {service.icon === 'user-md' && (
                                                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                                )}
                                            </svg>
                                        </motion.div>
                                        <div className="flex-1">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                                {service.title}
                                            </h4>
                                            <p className="text-gray-600 mb-3">
                                                {service.description}
                                            </p>
                                            <ul className="space-y-1">
                                                {service.features?.map((feature, featureIndex) => (
                                                    <li key={featureIndex} className="text-sm text-gray-500 flex items-center">
                                                        <svg className="w-4 h-4 text-accent-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    )
}

export default Services